import BottomSheet from '@components/BottomSheet'
import ContactForm from '@modules/ContactForm'
import CanisterForm from '@modules/TopUp/components/CanisterForm'
import { SendMode } from '@plug-core/hooks/useSend'
import useTopup from '@plug-core/hooks/useTopup'
import { Contact } from '@plug-core/interfaces/common'
import { AddressType } from '@plug-core/utils/addressMatcher'
import { shortAddress } from '@plug-core/utils/shortAddress'
import { Button, Input, Text, cn } from '@ui'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineUser } from 'react-icons/hi'

interface IProps {
  toAddress: string
  isValidAddress: boolean
  setToAddress: (address: string) => void
  matchedContact: Contact | undefined
  addressType: AddressType
  sendMode: SendMode
}

export const AddressTextField = ({
  toAddress,
  isValidAddress,
  setToAddress,
  matchedContact,
  addressType,
  sendMode,
}: IProps) => {
  const [contactModalVisible, setContactModalVisible] = useState(false)

  const { t } = useTranslation()
  const { addCanister } = useTopup()

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToAddress(e.target.value)
  }

  const onPaste: React.ClipboardEventHandler = (e) => {
    e.preventDefault()

    setToAddress(e.clipboardData.getData('Text').trim() || '')
  }

  const presentingValue = useMemo(() => {
    if (matchedContact) return matchedContact.name

    return isValidAddress ? shortAddress(toAddress, { leftSize: 12, rightSize: 3 }) : toAddress
  }, [isValidAddress, toAddress, matchedContact])

  const showContactSaveButton = addressType !== AddressType.Invalid && !matchedContact

  return (
    <>
      <Input
        mono
        autoFocus
        aria-label={t(`send.addressField.${sendMode}.placeholder`)}
        leading={
          <Text as="span" variant="label" tone="fg-2">
            {t(`send.addressField.${sendMode}.title`)}
          </Text>
        }
        trailing={
          showContactSaveButton && (
            <Button
              variant="link"
              size="sm"
              startIcon={<HiOutlineUser className="size-4 text-info" />}
              onClick={() => setContactModalVisible(true)}
            >
              {t('common.save')}
            </Button>
          )
        }
        placeholder={t(`send.addressField.${sendMode}.placeholder`)}
        className={cn(isValidAddress && 'font-semibold text-info')}
        value={presentingValue}
        onChange={handleAddressChange}
        onPaste={onPaste}
      />
      <BottomSheet
        visible={contactModalVisible}
        onBackdropClick={() => setContactModalVisible(false)}
      >
        {sendMode === SendMode.Send ? (
          <ContactForm address={toAddress} close={() => setContactModalVisible(false)} />
        ) : (
          <CanisterForm
            hideSelf={() => setContactModalVisible(false)}
            addCanister={addCanister}
            prefilledCanisterId={toAddress}
            newCanister
          />
        )}
      </BottomSheet>
    </>
  )
}
